import { JSX, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Autocomplete from "@mui/material/Autocomplete";

import RichTextEditor from "utils/elements/RichTextEditor";
import {
	ChangeEvent,
	DeleteEvent,
	Event,
	EventGroup,
	MoveEvent
} from "utils/history/Event";
import { ListInfo } from "utils/list/List";
import TableActionbar from "utils/list/TableActionbar";
import { asTitle } from "utils/Utils";

import { Competence } from "core/blueprint/competencelist/CompetenceListService";
import CompetenceTable from "core/blueprint/competencelist/CompetenceTable";
import { mergeLabels, sameLabels } from "core/blueprint/competencelist/Labels";

const NO_LABELS: string[] = [];

export type CompetenceExpansionProps = {
	criterion: Competence;
	editable: boolean;
	// The labels already used anywhere in the list, in document order.
	labels?: string[];

	onChange: (event: Event) => void;
};

export default function CompetenceExpansion(
	props: Readonly<CompetenceExpansionProps>
): JSX.Element {
	const { t } = useTranslation();
	const vocabulary = props.labels ?? NO_LABELS;

	const [listInfo, setListInfo] = useState<ListInfo<string>>(ListInfo.init());

	useEffect(() => {
		setListInfo(prevState =>
			prevState.withKeys(
				props.criterion.subcompetences.map(field => field.uuid)
			)
		);
	}, [props.criterion.subcompetences]);

	const changeField = useCallback(
		(event: Event): void => {
			event.applyContext("/subcompetences");
			props.onChange(event);
		},
		[props.onChange]
	);

	const selectFields = useCallback(
		(keys: string[], select: boolean): void => {
			setListInfo(previous => previous.markSelected(keys, select));
		},
		[listInfo, setListInfo]
	);

	const handleMoveUp = useCallback((): void => {
		const events: Array<Event> = [];
		for (const [index, field] of props.criterion.subcompetences.entries()) {
			if (listInfo.isVisibleAndSelected(field.uuid)) {
				events.push(
					new MoveEvent(
						`/subcompetences/${index}`,
						`/subcompetences/${index - 1}`
					)
				);
			}
		}
		const event = new EventGroup(events);
		props.onChange(event);
	}, [props.onChange, props.criterion.subcompetences, listInfo]);

	const handleMoveDown = useCallback((): void => {
		const events: Event[] = [];
		for (const [index, field] of props.criterion.subcompetences.entries()) {
			if (listInfo.isVisibleAndSelected(field.uuid)) {
				events.unshift(
					new MoveEvent(
						`/subcompetences/${index}`,
						`/subcompetences/${index + 1}`
					)
				);
			}
		}
		const event = new EventGroup(events);
		props.onChange(event);
	}, [props.onChange, props.criterion.subcompetences, listInfo]);

	const handleDelete = useCallback((): void => {
		const events: Event[] = [];
		for (const [index, field] of props.criterion.subcompetences.entries()) {
			if (listInfo.isVisibleAndSelected(field.uuid)) {
				events.unshift(
					new DeleteEvent(`/subcompetences/${index}`, field)
				);
			}
		}
		const event = new EventGroup(events);
		props.onChange(event);
	}, [props.onChange, props.criterion.subcompetences, listInfo]);

	const handleUpdateInfo = useCallback(
		(newInfo: string): void => {
			const event = new ChangeEvent(
				"/info",
				props.criterion.info,
				newInfo
			);
			props.onChange(event);
		},
		[props.onChange, props.criterion.info]
	);

	const handleUpdateLabels = useCallback(
		(_event: React.SyntheticEvent, values: string[]): void => {
			// The node's own labels lead the vocabulary, so an edit never
			// rewrites a label the node already has; a typed label that is
			// the same as an existing one takes the existing spelling.
			const merged = mergeLabels(values, [
				...props.criterion.labels,
				...vocabulary
			]);
			if (sameLabels(merged, props.criterion.labels)) {
				return;
			}
			const event = new ChangeEvent(
				"/labels",
				props.criterion.labels,
				merged
			);
			props.onChange(event);
		},
		[props.onChange, props.criterion.labels, vocabulary]
	);

	return (
		<Grid container direction="column" rowSpacing={2}>
			<Grid item>
				<Typography variant="subtitle2" aria-label="labels">
					{asTitle(t("labels"))}
				</Typography>
				<Autocomplete
					multiple
					freeSolo
					disableClearable
					openOnFocus
					filterSelectedOptions
					value={props.criterion.labels}
					disabled={!props.editable}
					onChange={handleUpdateLabels}
					renderInput={params => (
						<TextField {...params} variant="standard" />
					)}
					options={vocabulary}
				/>
			</Grid>

			<Grid item>
				<Typography variant="subtitle2" aria-label="info">
					{asTitle(t("info"))}
				</Typography>
				<RichTextEditor
					value={props.criterion.info}
					editable={props.editable}
					onChange={handleUpdateInfo}
				/>
			</Grid>

			<Grid item>
				<Typography variant="subtitle2" aria-label="elements">
					{asTitle(t("elements"))}
				</Typography>
				<TableActionbar
					listInfo={listInfo}
					onMoveUp={handleMoveUp}
					onMoveDown={handleMoveDown}
					onDelete={handleDelete}
				/>
				<CompetenceTable
					elements={props.criterion.subcompetences}
					labels={props.labels}
					listInfo={listInfo}
					editable={props.editable}
					onSelect={selectFields}
					onChange={changeField}
					onInsert={changeField}
				/>
			</Grid>
		</Grid>
	);
}
